import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { CartLineCollectionModel } from "@insite/client-framework/Types/ApiModels";
import addToCartCollectionTracking from "@MasterBluePrint/Tracking/AddToCart/addToCartCollectionTracking";
import { getOrderState } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import cancelOrder from "@insite/client-framework/Store/Pages/OrderDetails/Handlers/CancelOrder";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import OrderDetailPageTypeLink from "@insite/content-library/Components/OrderDetailPageTypeLink";
import { ShareEntityButtonStyles } from "@insite/content-library/Components/ShareEntityButton";
import { OrderDetailsPageContext } from "@insite/content-library/Pages/OrderDetailsPage";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import { OverflowMenuPresentationProps } from "@insite/mobius/OverflowMenu";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography from "@insite/mobius/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import reorder from "@MasterBluePrint/Overrides/Store/Pages/OrderDetails/Handlers/Reorder";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    buttonsOrder = "buttonsOrder",
    generateEmailAttachmentFromWebpage = "generateEmailAttachmentFromWebpage",
}

const enum buttons {
    reorder = "Reorder",
}

interface ButtonMapper {
    [key: string]: {
        button: any;
        clickable: any;
        showButtonOnTablet?: boolean;
    };
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.buttonsOrder]: ButtonModel[];
        [fields.generateEmailAttachmentFromWebpage]?: boolean;
    };
}

interface ButtonModel {
    fields: {
        name: string;
    };
}

const mapStateToProps = (state: ApplicationState) => {
    const order = getOrderState(state, state.pages.orderDetails.orderNumber).value;
    const settingsCollection = getSettingsCollection(state);
    return {
        order,
        canReorderItems: settingsCollection.orderSettings.canReorderItems,
        showAddToCartConfirmationDialog: settingsCollection.productSettings.showAddToCartConfirmationDialog,
        isReordering: state.pages.orderDetails.isReordering,
        state,
    };
};

const mapDispatchToProps = {
    cancelOrder,
    reorder,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

export interface OrderDetailsReorderButtonStyles {
    buttonsWrapper?: InjectableCss;
    buttonGridContainer?: GridContainerProps;
    overflowMenu?: OverflowMenuPresentationProps;
    shareEntityButtonStyles?: ShareEntityButtonStyles;
    reorderButton?: ButtonPresentationProps;
    spinner?: LoadingSpinnerProps;
    reorderClickable?: ClickablePresentationProps;
    reorderClickableSpinner?: LoadingSpinnerProps;
    buttonWrapper?: InjectableCss;
    buttonContainer?: InjectableCss;
}

export const orderDetailsReorderButtonStyles: OrderDetailsReorderButtonStyles = {
    buttonsWrapper: {
        css: css`
            display: flex;
            justify-content: center;
            clear: both;
            > div {
                > div {
                    margin-left: 0;
                }
            }
        `,
    },
    shareEntityButtonStyles: {
        button: {
            variant: "secondary",
        },
    },
    buttonContainer: {
        css: css`
            width: 100%;
            display: block;

            .spinner-container {
                margin: 15px 0 10px;
                display: flex;
                justify-content: center;
                align-items: center;
            }
        `,
    },
    buttonWrapper: {
        css: css`
            margin-left: 1.25rem;
        `,
    },
    reorderButton: {
        buttonType: "solid",
        variant: "primary",
        css: css`
            color: #333F48;
            font-family: 'Roboto', sans-serif;
            font-size: 14px;
            font-weight: bold;
            text-align: center;
            text-transform: uppercase;
            width: 100%;
            margin: 35px 0 15px;
        `,
    },
    spinner: {
        size: 22,
        css: css`
            margin: .5rem 3.125rem 0 2.125rem;
            display: flex;
            justify-content: center;
        `,
    },
    reorderClickableSpinner: {
        size: 22,
        css: css`
            margin: .625rem 0 .31250625rem .9375rem;
            display: flex;
            justify-content: center;
        `,
    },
};

/**
 * @deprecated Use orderDetailsButtonSetStyles instead.
 */
export const emailButtonStyles = orderDetailsReorderButtonStyles;
const styles = orderDetailsReorderButtonStyles;

const OrderDetailsReorderButton: React.FC<Props> = ({
    order,
    cancelOrder,
    reorder,
    fields,
    canReorderItems,
    showAddToCartConfirmationDialog,
    isReordering,
    history,
    state,
}) => {
    const toasterContext = React.useContext(ToasterContext);
    if (!order) {
        return null;
    }

    const onClickReorder = () => {
        reorder({
            onSuccess: (cartLineCollection: CartLineCollectionModel) => {
                if (cartLineCollection && cartLineCollection.cartLines && cartLineCollection.cartLines.length > 0) {
                    addToCartCollectionTracking(cartLineCollection, state);
                }

                if (!showAddToCartConfirmationDialog) {
                    return;
                }
                toasterContext.addToast({
                    children: (
                        <>
                            <OrderDetailPageTypeLink
                                title={order.webOrderNumber}
                                orderNumber={order.webOrderNumber || order.erpOrderNumber}
                            />
                            &nbsp;{translate("Added to Cart")}
                        </>
                    ),
                    messageType: "success",
                    timeoutLength: 6000,
                });
            },
            onError: (error: any) => {
                if (error === 'cart_quote') {
                    toasterContext.addToast({ body: translate(error), messageType: "danger" });
                } else {
                    toasterContext.addToast({ body: siteMessage("Core_NotEnoughStockError"), messageType: "danger" });
                }
            }
        });
    };

    const buttonList: ButtonMapper = {};

    if (order.canAddToCart && canReorderItems) {
        buttonList[buttons.reorder] = {
            button: (
                <>
                    <StyledWrapper className="totalReorderContainer">
                        {!isReordering && (
                            <Button className="totalReorderbtn"
                                {...styles.reorderButton}
                                onClick={onClickReorder}
                                disabled={isReordering}
                                data-test-selector="orderDetailsReorderButton"
                            >
                                {translate("Reorder")}
                            </Button>
                        )}
                        <Typography as="span" className="spinner-container">{isReordering && <LoadingSpinner {...styles.spinner} />}</Typography>
                    </StyledWrapper>


                </>
            ),
            clickable: (
                <>
                    {!isReordering && (
                        <Clickable {...styles.reorderClickable} onClick={onClickReorder} disabled={isReordering}>
                            {translate("Reorder")}
                        </Clickable>
                    )}
                    {isReordering && <LoadingSpinner {...styles.reorderClickableSpinner} />}
                </>
            ),
            showButtonOnTablet: true,
        };
    }

    const buttonsToRender = fields.buttonsOrder.map(button => buttonList[button.fields.name]).filter(button => button);

    return (
        <StyledWrapper {...styles.buttonsWrapper} className="total-reorder-btn-wrapper">
            <Typography as="div" {...styles.buttonContainer}>
                {buttonsToRender.map((button, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <StyledWrapper {...styles.buttonWrapper} key={index}>
                        {button.button}
                    </StyledWrapper>
                ))}
            </Typography>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(OrderDetailsReorderButton)),
    definition: {
        allowedContexts: [OrderDetailsPageContext, "OrderStatusPage"],
        displayName: "Order Details Reorder Button",
        group: "Order Details",
        fieldDefinitions: [
            {
                name: fields.buttonsOrder,
                displayName: "Reorder Button",
                editorTemplate: "ListField",
                defaultValue: [
                    { fields: { name: buttons.reorder } },
                ],
                getDisplay: (item: HasFields) => {
                    return item.fields.name;
                },
                hideAdd: true,
                hideEdit: true,
                hideDelete: true,
                fieldType: "General",
                sortOrder: 1,
                fieldDefinitions: [
                    {
                        name: "name",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                ],
            },
        ],
    },
};

export default widgetModule;